import {EnkelGruppeModell, GruppeModell, GruppeRessursClient, Gruppetype, KopierMøteTilGrupperSpesifikasjon, KopierMøteTilGrupperValideringsfeil, MøteId, MøteModell, MøteRessursClient, OpprettMøteValideringsfeil, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {Temporal} from "temporal-polyfill";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {ChangeDetectionStrategy, Component, Inject, Injectable, signal} from "@angular/core";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatToolbarModule} from "@angular/material/toolbar";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {MatInputModule} from "@angular/material/input";
import {MatFormFieldModule} from "@angular/material/form-field";
import {TranslateModule} from "@ngx-translate/core";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {GjeldendeTidssone, Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {TypetValideringsresultatModell} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {antallInnslagIListe, erIkkeTomListe} from "felles-ng2/støttefunksjoner/lister";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {DatoKlokkeslettVelgerKomponent} from "web-ng2/delt/komponenter/dato-klokkeslett-velger/dato-klokkeslett-velger";
import {VelgGruppeKomponent} from "web-ng2/moduler/gruppe/komponenter/velg-gruppe/velg-gruppe";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";
import {lesMøtedato} from "felles-ng2/modell/møteModellHjelpere";
import ZonedDateTime = Temporal.ZonedDateTime;

export type KopierMøteDialogRespons = { opprettedeMøter: MøteModell[] } | null;
const debug = initDebugger('KopierMøteDialogKomponent');


@Component({
    selector: 'kopier-moete-dialog',
    templateUrl: 'kopier-møte-dialog.html',
    styleUrls: ['kopier-møte-dialog.scss'],
    providers: [],
    imports: [
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatToolbarModule,
        VisSpinnerDirektiv,
        ReactiveFormsModule,
        MatInputModule,
        MatFormFieldModule,
        TranslateModule,
        TipsKomponent,
        DatoKlokkeslettVelgerKomponent,
        VelgGruppeKomponent,
        InlineMeldingKomponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KopierMøteDialogKomponent {

    static vis(dialog: MatDialog, møte: MøteModell, møtetsGruppe: EnkelGruppeModell): MatDialogRef<KopierMøteDialogKomponent, KopierMøteDialogRespons> {
        return dialog.open(KopierMøteDialogKomponent, {data: {møte, møtetsGruppe} as DialogParametre, disableClose: true, ariaLabelledBy: 'kopier-møte-dialog-tittel', minHeight: 600, width: '600px'});
    }

    skjema = this.formBuilder.group({
        grupper: [<GruppeModell[]>[], [Validators.required, Validators.minLength(1)]],
        dato: [<ZonedDateTime | null>null, Validators.required]
    })

    /** gruppene som innlogget bruker kan kopierer møter til (grupper som bruker har rediger-tilgang til) */
    tilgjengeligeGrupper: GruppeModell[] = [];
    moete: MøteModell;
    opprettedeMoeter: MøteModell[] = [];
    feilPerGruppe: { gruppe: GruppeModell; valideringsfeil: string }[] = [];

    modus = signal<'skjema' | 'kvittering'>('skjema');
    harFeil = signal(false);
    lagrer = signal(false);

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private avhengigheter: KopierMøteDialogKomponentAvhengigheter,
                public dialogRef: MatDialogRef<KopierMøteDialogKomponent, KopierMøteDialogRespons>,
                private formBuilder: FormBuilder,
                private navigasjon: Navigasjon,
                private oversettelser: Oversettelser) {
        this.moete = parametre.møte;
        const _møtedato = lesMøtedato(parametre.møte);
        this.tilgjengeligeGrupper = [parametre.møtetsGruppe as GruppeModell];
        this.skjema.patchValue({dato: _møtedato.start, grupper: [parametre.møtetsGruppe as GruppeModell]});
        avhengigheter.tilgjengeligeGrupper().then(grupper => {
            this.tilgjengeligeGrupper = grupper
            this.skjema.patchValue({grupper: [this.gruppe(this.moete)]});
        });
    }

    get antallBeslutninger() {
        return this.moete.saker.map(s => antallInnslagIListe(s.beslutninger)).reduce((a, b) => a + b, 0);
    }

    get antallDokumenter() {
        return this.moete.saker.map(s => antallInnslagIListe(s.dokumenter)).reduce((a, b) => a + b, 0);
    }

    gruppe(møte: MøteModell): GruppeModell {
        return this.tilgjengeligeGrupper.find(kandidat => kandidat.id == møte.eiendeGruppeId)!;
    }

    gaaTilMoete(møte: MøteModell) {
        this.navigasjon.gåTilMøte(møte.id);
    }

    kopier() {
        this.lagrer.set(true);
        this.avhengigheter.valider(this.moete.id, this.somSpesifikasjon())
          .then(resultat => this.setValideringsfeil(resultat))
          .then(() => {
              if (this.harFeil()) return <MøteModell[]>[];
              return this.avhengigheter.kopier(this.moete.id, this.somSpesifikasjon());
          })
          .then(opprettedeMøter => {
              if (this.harFeil()) return;
              debug('opprett, opprettede møter: %o', opprettedeMøter);
              this.opprettedeMoeter = opprettedeMøter;
              //det holder at vi broadcaster for ett møte - dette vil oppdatere horisontalMøtenavigasjon komponenten
              this.avhengigheter.broadcastLokalMøteOpprettet(opprettedeMøter[0]);
              this.modus.set('kvittering');
          })
          .finally(() => this.lagrer.set(false));
    }

    avbryt() {
        this.dialogRef.close(null);
    }

    ferdig() {
        this.dialogRef.close({opprettedeMøter: this.opprettedeMoeter});
    }

    // private hjelpemetoder

    private somSpesifikasjon(): KopierMøteTilGrupperSpesifikasjon {
        const verdier = this.skjema.value;
        return {
            startdato: verdier.dato!.toJSON(),
            gruppeIdr: verdier.grupper!.map(gruppe => gruppe.id)
        }
    }

    private setValideringsfeil(resultat: KopierMøteTilGrupperValideringsfeil) {
        //console.log('### setValideringsfeil', resultat);
        this.feilPerGruppe = Object.entries(resultat.feil)
          .map(gruppeIdOgFeil => ({
              gruppe: this.tilgjengeligeGrupper.find(gruppe => gruppe.id == gruppeIdOgFeil[0])!,
              valideringsfeil: this.valideringsmelding(gruppeIdOgFeil[1] as TypetValideringsresultatModell<OpprettMøteValideringsfeil>)!
          }))
          .filter(feil => erIkkeNil(feil.valideringsfeil));
        this.harFeil.set(erIkkeTomListe(this.feilPerGruppe));
    }

    private valideringsmelding(feilPåGruppe: TypetValideringsresultatModell<OpprettMøteValideringsfeil>): string|null {
        //console.log('### valideringsmelding', feilPåGruppe);
        if (feilPåGruppe.feil.some(feil => feil.feilkode === OpprettMøteValideringsfeil.protokoll_låst))
            return this.oversettelser.oversett(i18n('Protokoll fra tidligere møte er låst, du kan ikke opprette nye møter før dette'));
        if (feilPåGruppe.feil.some(feil => feil.feilkode === OpprettMøteValideringsfeil.møte_opprett_samme_starttidspunkt))
            return this.oversettelser.oversett(i18n('Møte overlapper annet møte'));
        //ignorerer l.OpprettMøteValideringsfeil.møte_opprett_ingen_plan
        return null;
    }
}


export interface DialogParametre {
    møte: MøteModell;
    møtetsGruppe: EnkelGruppeModell;
}


@Injectable()
export class KopierMøteDialogKomponentAvhengigheter {

    constructor(private møteRessurs: MøteRessursClient,
                private gruppeRessurs: GruppeRessursClient,
                private modelloppdateringer: Modelloppdateringer,
                private brukerkontekst: BrukerkontekstTilbyder,
                private gjeldendeTidssone: GjeldendeTidssone) {
    }

    get tidssone(): Tidssoneinformasjon {
        return this.gjeldendeTidssone.brukersTidssone();
    }

    /** Liste med grupper bruker har skrivetilgang til */
    async tilgjengeligeGrupper(): Promise<GruppeModell[]> {
        const grupper = await this.gruppeRessurs.finnTilgjengeligeGrupper().somPromise();
        //@todo tilgjengelige grupper kunne vært hentet fra kontekst? - uten oppslag mot server
        return grupper
          .filter(gruppe => this.brukerkontekst.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, gruppe.id))
          .filter(gruppe => gruppe.gruppetype == Gruppetype.ordinær);
    }

    valider(kildemøte: MøteId, spesifikasjon: KopierMøteTilGrupperSpesifikasjon): Promise<KopierMøteTilGrupperValideringsfeil> {
        debug('valider %j', spesifikasjon)
        return this.møteRessurs.validerKopierMøte(kildemøte, spesifikasjon)
          .somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke validere nytt møte')) as Promise<KopierMøteTilGrupperValideringsfeil>;
    }

    kopier(kildemøte: MøteId, spesifikasjon: KopierMøteTilGrupperSpesifikasjon): Promise<MøteModell[]> {
        debug('KopierMøteDialogKomponentAvhengigheter.kopier', spesifikasjon)
        return this.møteRessurs.kopier(kildemøte, spesifikasjon)
          .somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke opprette møte'));
    }

    broadcastLokalMøteOpprettet(møte: MøteModell): void {
        this.modelloppdateringer.broadcastLokalNyttMøte(møte.id);
    }
}
